import type { ConversationListItem } from '@/entities/conversations';
import { apiClient } from '@/shared/api';
import type { SearchResult } from '../types';
import { search } from '../api';
import type {
  SearchConversationDeleteRequest,
  SearchConversationDeleteResult,
  SearchConversationDownloadRequest,
  SearchConversationRequest,
  SearchConversationRunPipelineRequest,
  SearchConversationRunPipelineResponse,
} from './types';

export const searchConversations = async (
  request: Omit<SearchConversationRequest, 'entityType'>,
): Promise<SearchResult<ConversationListItem>> => {
  return search<ConversationListItem>({
    ...request,
    entityType: 'conversation',
  });
};

/**
 * Выгрузка найденных диалогов файлом через POST /search/query/download.
 * Тело — та же выборка, что и у поиска, плюс вид таблицы на экране; постраничности нет.
 * Файл приходит один, `.xlsx`: формат не выбирается.
 *
 * `silentError` подавляет общее всплывающее уведомление: причина отказа приходит телом ответа,
 * которое читается файлом, и общее уведомление показало бы вместо неё безымянную ошибку.
 * О неудаче сообщает сам вызывающий, разобрав тело ответа.
 */
export const downloadConversationsSearch = async (
  request: Omit<SearchConversationDownloadRequest, 'entityType'>,
): Promise<Blob> => {
  const { data } = await apiClient.post<Blob>(
    '/search/query/download',
    { ...request, entityType: 'conversation' },
    { responseType: 'blob', silentError: true },
  );
  return data;
};

/**
 * Массовый запуск обработки диалогов через POST /search/query/run-pipeline.
 * Тело — та же выборка, что и у поиска, плюс выбранный пайплайн; постраничности нет.
 */
export const runConversationsPipeline = async (
  request: Omit<SearchConversationRunPipelineRequest, 'entityType'>,
): Promise<SearchConversationRunPipelineResponse> => {
  const { data } = await apiClient.post<SearchConversationRunPipelineResponse>('/search/query/run-pipeline', {
    ...request,
    entityType: 'conversation',
  });
  return data;
};

/**
 * Удаление выбранных диалогов через POST /search/query/delete.
 * Удаляются только диалоги из `filters.conversationIds`, доступные пользователю; все разом или ни один.
 */
export const deleteConversations = async (
  request: Omit<SearchConversationDeleteRequest, 'entityType'>,
): Promise<SearchConversationDeleteResult> => {
  const { data } = await apiClient.post<SearchConversationDeleteResult>('/search/query/delete', {
    ...request,
    entityType: 'conversation',
  });
  return data;
};
